import React, { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  ErrorAlert,
  linkVariants
} from '@companion-professional/components';
import { cn } from '@companion-professional/webutils';
import { LoginFormValues } from '../../hooks/useLogin';
import { LoginHeading } from '../../components/LoginHeading';

export interface LoginFormProps {
  onFormSubmit?: (data: LoginFormValues) => void;

  // initialValues is an object that contains the initial values for the form. This might be used to pre-populate the
  // email address field if the user has previously logged in and selected "remember me".  Defaults to empty object.
  initialValues?: Partial<LoginFormValues>;

  // isLoggingIn is a boolean that indicates if the user is currently logging in.  This is used to disable the form
  // while the login is in progress.  Defaults to false.
  isLoggingIn?: boolean;

  // errorMessage is a string that contains an error message to display to the user (eg a failed login attempt).
  errorMessage: string | null;
}

// ForgotPasswordDialog is the "Forgot Password?" link along with the dialog it opens.  We don't have a self-service
// password reset flow yet, so the dialog just tells the user how to request a reset from support.
const ForgotPasswordDialog: FunctionComponent = () => (
  <Dialog>
    <DialogTrigger asChild>
      {/* type="button" keeps this from submitting the login form it lives inside. */}
      <button type="button" className={cn(linkVariants(), 'text-sm')}>
        Forgot Password?
      </button>
    </DialogTrigger>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Forgot Password</DialogTitle>
        <DialogDescription>
          Please email support@cv.vet from the email address you use for your login to ask for a password reset.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="sm:justify-start">
        <DialogClose asChild>
          <Button size="small" type="button" variant="secondary">
            Close
          </Button>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// LoginForm is the login form component.  See the LoginFormProps interface (in the <Login /> component for the props
// that can be passed to this component).
export const LoginForm: FunctionComponent<LoginFormProps> = ({
  initialValues = {},
  onFormSubmit = (values: LoginFormValues) => {
    console.log('Form submitted with values: ', values);
  },
  isLoggingIn = false,
  errorMessage
}) => {
  const { register, handleSubmit, setValue, watch } = useForm<LoginFormValues>({ defaultValues: initialValues });
  const rememberChecked = watch('remember');

  return (
    <div className="flex-1 bg-widget p-6 md:p-8">
      <LoginHeading title="Account Login" />
      <form className="flex flex-col space-y-5" onSubmit={handleSubmit(onFormSubmit)}>
        {errorMessage ? <ErrorAlert title="Error Logging In" message={errorMessage} /> : null}
        <div className="flex flex-col space-y-1">
          <Label htmlFor="email">Email Address</Label>
          <Input
            {...register('email')}
            type="email"
            id="email"
            placeholder="name@company.com"
            disabled={isLoggingIn}
            autoCorrect="off"
            autoCapitalize="none"
          />
        </div>
        <div className="flex flex-col space-y-1">
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <ForgotPasswordDialog />
          </div>
          <Input {...register('password')} id="password" type="password" disabled={isLoggingIn} />
        </div>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="remember"
            checked={rememberChecked}
            onClick={() => {
              setValue('remember', !rememberChecked);
            }}
            disabled={isLoggingIn}
          />
          <Label htmlFor="remember">Remember me</Label>
        </div>
        <div>
          <Button type="submit" size="large" className="w-full" loading={isLoggingIn}>
            {isLoggingIn ? 'Logging In...' : 'Log In'}
          </Button>
        </div>
      </form>
    </div>
  );
};
